import { useEffect, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { ThemeProvider } from "@mui/material/styles";
import { createUseStyles } from "react-jss";
import { TestSessionRecord } from "../helpers/db";
import {
  formatHskLevels,
  formatMissRate,
  formatSessionWhen,
  NeedsWorkItem
} from "../helpers/progress";
import { getProgressOverview, getTestResult, ProgressOverview, SavedTestResult } from "../helpers/storage";
import { HskEntry } from "../helpers/types";
import { menuColors, menuTheme } from "../theme/menuTheme";
import TestSessionDetail from "./TestSessionDetail";

type MoreView = "home" | "needsWork" | "history" | "session" | "terms" | "privacy" | "licenses";

const useStyles = createUseStyles({
  page: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    textAlign: "left",
    padding: "16px 16px 24px",
    color: "#e2e8f0"
  },
  pageFlush: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    color: "#e2e8f0"
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginBottom: 8
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 600
  },
  lead: {
    margin: "0 0 20px",
    fontSize: 14,
    lineHeight: 1.45,
    color: "#94a3b8"
  },
  section: {
    margin: "0 0 18px"
  },
  heading: {
    margin: "0 0 6px",
    fontSize: 15,
    fontWeight: 600
  },
  body: {
    margin: "0 0 12px",
    fontSize: 14,
    lineHeight: 1.45,
    color: "#cbd5e1"
  },
  muted: {
    color: "#94a3b8"
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    margin: "8px 0 20px"
  },
  copyright: {
    margin: "24px 0 0",
    fontSize: 12,
    lineHeight: 1.4,
    color: "#64748b"
  },
  licenseName: {
    margin: "12px 0 4px",
    fontSize: 14,
    fontWeight: 600,
    color: "#e2e8f0"
  },
  rowButton: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "10px 0",
    border: "none",
    borderBottom: "1px solid #404754",
    background: "transparent",
    color: "#e2e8f0",
    textAlign: "left",
    cursor: "pointer"
  }
});

const TERMS = [
  "Mandarin Flashcards is a study tool for reviewing HSK characters. By using the app you agree to these terms.",
  "The app is provided as-is for personal study. We do not guarantee that character data, pinyin, or definitions are complete or error-free.",
  "You are responsible for how you use the app. Do not rely on it as your only source for exams or official HSK preparation.",
  "Progress and settings are stored on your device. Clearing site data will erase them.",
  "We may update the app and these terms. Continued use after an update means you accept the current terms."
];

const PRIVACY = [
  "This app is designed to run in your browser. Study progress, test history, filters, and settings are saved on this device. They are not sent to a server.",
  "We do not ask you to create an account. We do not sell your personal information.",
  "If you host or share a copy of this app, your hosting provider’s logs (such as IP address and browser type) may be collected according to that provider’s policy.",
  "You can delete stored data by clearing this site’s data in your browser settings.",
  "If we add online features later, we will update this policy before collecting new kinds of data."
];

const LICENSES: { name: string; license: string }[] = [
  { name: "React", license: "MIT" },
  { name: "React DOM", license: "MIT" },
  { name: "MUI (Material UI, icons, Emotion)", license: "MIT" },
  { name: "Jotai", license: "MIT" },
  { name: "React Router", license: "MIT" },
  { name: "react-jss", license: "MIT" },
  { name: "react-swipeable", license: "MIT" },
  { name: "TypeScript", license: "Apache-2.0" },
  { name: "Create React App / react-scripts", license: "MIT" }
];

const LEGAL_TITLES: Record<"terms" | "privacy" | "licenses", string> = {
  terms: "Terms of service",
  privacy: "Privacy policy",
  licenses: "Open source licenses"
};

const ScoreSparkline = ({ percents }: { percents: number[] }) => {
  const width = 240;
  const height = 44;
  const points = percents
    .map((value, index) => {
      const x = percents.length === 1 ? width / 2 : (index / (percents.length - 1)) * width;
      const y = height - (value / 100) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-label="Score by session">
      <polyline fill="none" stroke={menuColors.accent} strokeWidth="2" points={points} />
    </svg>
  );
};

const HistoryRow = ({
  session,
  onClick
}: {
  session: TestSessionRecord;
  onClick: () => void;
}) => {
  const classes = useStyles();
  const answered = session.correctCount + session.incorrectCount;
  const pct = answered > 0 ? Math.round((session.correctCount / answered) * 100) : 0;

  return (
    <button type="button" className={classes.rowButton} onClick={onClick}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: 13 }}>{formatSessionWhen(session.finishedAt)}</Box>
        <Box sx={{ fontSize: 11, color: menuColors.textMuted }}>{formatHskLevels(session.filteredLevels)}</Box>
      </Box>
      <Box sx={{ textAlign: "right" }}>
        <Box sx={{ fontSize: 13, fontWeight: 600, color: menuColors.accent }}>{pct}%</Box>
        <Box sx={{ fontSize: 11, color: menuColors.textMuted }}>
          {session.correctCount}/{session.totalCount}
        </Box>
      </Box>
    </button>
  );
};

const NeedsWorkRow = ({ item }: { item: NeedsWorkItem }) => {
  const classes = useStyles();
  return (
    <div className={classes.rowButton} style={{ cursor: "default" }}>
      <Box sx={{ fontSize: 22, width: 32, color: menuColors.text }}>{item.card.charSimp}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: 13, color: menuColors.accent }}>{item.card.pinyin}</Box>
        <Box sx={{ fontSize: 11, color: menuColors.textMuted }}>{item.card.meaning}</Box>
      </Box>
      <Box sx={{ fontSize: 12, fontWeight: 600, color: "#F0997B" }}>{formatMissRate(item.missRate)}</Box>
    </div>
  );
};

const SubpageHeader = ({
  title,
  onBack,
  trailing
}: {
  title: string;
  onBack: () => void;
  trailing?: string;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.titleRow}>
      <IconButton aria-label="Back" onClick={onBack} sx={{ color: "#e2e8f0", ml: -1 }}>
        <ChevronLeftIcon />
      </IconButton>
      <h1 className={classes.title}>{title}</h1>
      {trailing ? (
        <Box sx={{ ml: "auto", fontSize: 13, color: menuColors.textMuted }}>{trailing}</Box>
      ) : null}
    </div>
  );
};

const MorePage = ({ onStartCustomTest }: { onStartCustomTest: (cards: HskEntry[]) => void }) => {
  const classes = useStyles();
  const [view, setView] = useState<MoreView>("home");
  const [sessionBack, setSessionBack] = useState<"home" | "history">("home");
  const [overview, setOverview] = useState<ProgressOverview | null>(null);
  const [sessionResult, setSessionResult] = useState<SavedTestResult | null>(null);
  const [progressOpen, setProgressOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const next = await getProgressOverview();
      if (!cancelled) {
        setOverview(next);
        setProgressOpen(next.testCount > 0);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const openSession = async (sessionId: string, back: "home" | "history") => {
    const result = await getTestResult(sessionId);
    if (!result) {
      return;
    }
    setSessionResult(result);
    setSessionBack(back);
    setView("session");
  };

  const goHome = () => {
    setView("home");
    setSessionResult(null);
  };

  if (view === "session" && sessionResult) {
    return (
      <ThemeProvider theme={menuTheme}>
        <section className={classes.pageFlush} aria-label="Test session">
          <TestSessionDetail
            result={sessionResult}
            onBack={() => {
              setSessionResult(null);
              setView(sessionBack);
            }}
            onRetryMissed={onStartCustomTest}
            onRetryTest={onStartCustomTest}
          />
        </section>
      </ThemeProvider>
    );
  }

  if (view === "needsWork" && overview) {
    return (
      <ThemeProvider theme={menuTheme}>
        <section className={classes.page} aria-label="Needs work">
          <SubpageHeader
            title="Needs work"
            onBack={goHome}
            trailing={`${overview.needsWork.length}`}
          />
          <p className={`${classes.body} ${classes.muted}`}>
            Ranked by miss rate across every finished test on this device.
          </p>
          {overview.needsWork.map((item) => (
            <NeedsWorkRow key={item.card.order} item={item} />
          ))}
          {overview.needsWork.length > 0 ? (
            <Button
              fullWidth
              variant="contained"
              onClick={() => onStartCustomTest(overview.needsWork.map((item) => item.card))}
              sx={{ mt: 2, textTransform: "none", fontWeight: 600 }}>
              {`Quiz these ${overview.needsWork.length}`}
            </Button>
          ) : null}
        </section>
      </ThemeProvider>
    );
  }

  if (view === "history" && overview) {
    return (
      <ThemeProvider theme={menuTheme}>
        <section className={classes.page} aria-label="Test history">
          <SubpageHeader title="Test history" onBack={goHome} />
          {overview.history.map((session) => (
            <HistoryRow
              key={session.id}
              session={session}
              onClick={() => {
                void openSession(session.id, "history");
              }}
            />
          ))}
        </section>
      </ThemeProvider>
    );
  }

  if (view === "terms" || view === "privacy" || view === "licenses") {
    const paragraphs = view === "terms" ? TERMS : view === "privacy" ? PRIVACY : null;
    return (
      <ThemeProvider theme={menuTheme}>
        <section className={classes.page} aria-label={LEGAL_TITLES[view]}>
          <SubpageHeader title={LEGAL_TITLES[view]} onBack={goHome} />
          {paragraphs
            ? paragraphs.map((text) => (
                <p key={text.slice(0, 24)} className={classes.body}>
                  {text}
                </p>
              ))
            : LICENSES.map((entry) => (
                <div key={entry.name}>
                  <p className={classes.licenseName}>{entry.name}</p>
                  <p className={`${classes.body} ${classes.muted}`}>{entry.license} License</p>
                </div>
              ))}
          {view === "licenses" ? (
            <p className={`${classes.body} ${classes.muted}`}>
              Direct dependencies are listed above. Transitive packages ship with their own notices in
              this project’s dependency tree.
            </p>
          ) : null}
        </section>
      </ThemeProvider>
    );
  }

  const summary =
    overview == null
      ? ""
      : overview.testCount > 0
        ? `${overview.accuracyPercent ?? 0}% accuracy · ${overview.testCount} test${
            overview.testCount === 1 ? "" : "s"
          }`
        : "Finish a test to track scores and missed words.";

  return (
    <ThemeProvider theme={menuTheme}>
      <section className={classes.page} aria-label="About">
        <h1 className={classes.title}>More</h1>
        <p className={classes.lead}>
          A compact place to revise HSK characters: browse a page of cards, or quiz one at a time.
        </p>

        <Accordion
          expanded={progressOpen}
          onChange={(_, open) => setProgressOpen(open)}
          disableGutters
          elevation={0}
          sx={{
            mb: 3,
            border: `1px solid ${menuColors.border}`,
            borderRadius: "8px !important",
            overflow: "hidden",
            "&:before": { display: "none" }
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 1.5,
              minHeight: 52,
              "& .MuiAccordionSummary-content": {
                my: 1,
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.25
              }
            }}>
            <Box sx={{ fontSize: 16, fontWeight: 600, color: menuColors.text }}>Progress</Box>
            <Box sx={{ fontSize: 13, color: menuColors.textMuted }}>{summary}</Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
            {overview && overview.testCount > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ fontSize: 22, fontWeight: 700, color: menuColors.accent }}>
                      {overview.accuracyPercent ?? 0}%
                    </Box>
                    <Box sx={{ fontSize: 11, color: menuColors.textMuted }}>
                      accuracy · {overview.testCount} tests
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ fontSize: 22, fontWeight: 700, color: menuColors.text }}>
                      {overview.cardsSeen}
                    </Box>
                    <Box sx={{ fontSize: 11, color: menuColors.textMuted }}>cards marked</Box>
                  </Box>
                </Box>
                {overview.scores.length > 1 ? (
                  <Box>
                    <Box sx={{ fontSize: 11, color: menuColors.textMuted, mb: 0.5 }}>Score by session</Box>
                    <ScoreSparkline percents={overview.scores.map((entry) => entry.percent)} />
                  </Box>
                ) : null}
                {overview.needsWork.length > 0 ? (
                  <Button
                    fullWidth
                    onClick={() => setView("needsWork")}
                    sx={{
                      display: "block",
                      textAlign: "left",
                      textTransform: "none",
                      p: 1.25,
                      border: `1px solid ${menuColors.border}`,
                      borderRadius: 2,
                      backgroundColor: menuColors.surfaceRaised,
                      color: menuColors.text
                    }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Box sx={{ fontSize: 13, fontWeight: 600 }}>Needs work</Box>
                      <Box sx={{ fontSize: 12, color: menuColors.textMuted }}>
                        {overview.needsWork.length} words
                      </Box>
                    </Box>
                    <Box sx={{ mt: 0.5, fontSize: 12, color: menuColors.textMuted }}>
                      {overview.needsWork
                        .slice(0, 3)
                        .map((item) => `${item.card.charSimp} ${formatMissRate(item.missRate)}`)
                        .join(" · ")}
                    </Box>
                  </Button>
                ) : null}
                {overview.history.length > 0 ? (
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Box sx={{ fontSize: 13, fontWeight: 600, color: menuColors.text }}>History</Box>
                      {overview.history.length > 3 ? (
                        <Button
                          size="small"
                          onClick={() => setView("history")}
                          sx={{ textTransform: "none", minWidth: 0, p: 0, fontWeight: 600 }}>
                          See all
                        </Button>
                      ) : null}
                    </Box>
                    {overview.history.slice(0, 3).map((session) => (
                      <HistoryRow
                        key={session.id}
                        session={session}
                        onClick={() => {
                          void openSession(session.id, "home");
                        }}
                      />
                    ))}
                  </Box>
                ) : null}
              </Box>
            ) : (
              <TypographyQuiet />
            )}
          </AccordionDetails>
        </Accordion>

        <div className={classes.section}>
          <h2 className={classes.heading}>Study</h2>
          <p className={classes.body}>
            Flip a row to see the other side. Hold a card for extra detail. Use the header to switch
            between card list and grid.
          </p>
        </div>

        <div className={classes.section}>
          <h2 className={classes.heading}>Test</h2>
          <p className={classes.body}>
            Set up a deck on the Test tab, then start. One character at a time, with a score. Finish
            whenever you like.
          </p>
        </div>

        <div className={classes.section}>
          <h2 className={classes.heading}>Legal</h2>
          <div className={classes.actions}>
            <Button variant="outlined" fullWidth onClick={() => setView("terms")}>
              Terms of service
            </Button>
            <Button variant="outlined" fullWidth onClick={() => setView("privacy")}>
              Privacy policy
            </Button>
            <Button variant="outlined" fullWidth onClick={() => setView("licenses")}>
              Open source licenses
            </Button>
          </div>
        </div>

        <p className={classes.copyright}>
          © {new Date().getFullYear()} Mandarin Flashcards. All rights reserved. HSK character data is
          provided for personal study.
        </p>
      </section>
    </ThemeProvider>
  );
};

const TypographyQuiet = () => (
  <Box sx={{ fontSize: 13, color: menuColors.textMuted }}>
    Scores, missed words, and history will show up here after you finish a test.
  </Box>
);

export default MorePage;
